import { useState } from 'react';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';

/**
 * MCP-01 en la web: la dirección del conector de Athena para usarla desde ChatGPT o Claude.
 *
 * **El interruptor es `VITE_MCP_URL`**: vacía, no se enseña nada (ni aquí ni en /como-funciona);
 * con la dirección puesta en Render, aparece. Así no se anuncia una conexión que todavía no funciona
 * (falta activar el OAuth de Supabase) y encenderlo es poner una variable, sin tocar código.
 */
export const URL_DEL_CONECTOR: string = import.meta.env.VITE_MCP_URL ?? '';
export const CONECTOR_VISIBLE = URL_DEL_CONECTOR.trim() !== '';

/**
 * Para quien tiene cuenta, en «Mi cuenta»: qué gana (preguntar desde su asistente y recibir las
 * cifras con su estudio y su página), la dirección con un botón de copiar y, plegados, los pasos
 * para cada asistente. «MCP» solo sale como dato técnico al final, para quien lo busque.
 */
export function ConectorAsistentes({ idioma, asistenteInicial }: { readonly idioma: Idioma; readonly asistenteInicial?: string | null }) {
  const t = diccionarioDe(idioma).cuentas.asistentes;
  // Qué se acaba de copiar: la dirección o el texto para el agente. Un solo aviso a la vez.
  const [copiado, setCopiado] = useState<'direccion' | 'prompt' | null>(null);
  if (!CONECTOR_VISIBLE) return null;

  const promptAgente = t.agentes.prompt.replace('{url}', URL_DEL_CONECTOR);
  const copiar = (texto: string, cual: 'direccion' | 'prompt') => {
    void navigator.clipboard
      .writeText(texto)
      .then(() => {
        setCopiado(cual);
        setTimeout(() => {
          setCopiado(null);
        }, 2000);
      })
      .catch(() => undefined);
  };

  return (
    <section aria-labelledby='asistentes-titulo' className='flex flex-col gap-3 border-t border-(--line) pt-6'>
      <h2 id='asistentes-titulo' className='m-0 font-(family-name:--serif) text-[22px] leading-[1.2] font-bold text-(--ink)'>
        {t.titulo}
      </h2>
      <p className='m-0 font-(family-name:--text) text-[17px] leading-[1.55] text-(--ink-soft)'>{t.explicacion}</p>

      <div className='flex items-stretch gap-2'>
        <label htmlFor='url-conector' className='sr-only'>
          {t.direccion}
        </label>
        <input
          id='url-conector'
          readOnly
          value={URL_DEL_CONECTOR}
          onFocus={e => {
            e.currentTarget.select();
          }}
          className='min-w-0 grow rounded-[3px] border border-(--line-strong) bg-(--chat-bg) px-3 py-2 font-(family-name:--mono) text-[15px] text-(--ink)'
        />
        <button
          type='button'
          onClick={() => {
            copiar(URL_DEL_CONECTOR, 'direccion');
          }}
          className='shrink-0 cursor-pointer rounded-full border border-(--line-strong) px-4 py-2 font-(family-name:--text) text-[16px] text-(--ink) hover:bg-(--chat-bg)'
        >
          {copiado === 'direccion' ? t.copiado : t.copiar}
        </button>
      </div>
      <p aria-live='polite' className='sr-only'>
        {copiado === null ? '' : t.copiado}
      </p>

      {/* Un desplegable por asistente y, dentro, cuenta personal y de empresa: en empresa lo añade
          primero quien administra, y luego cada persona lo conecta con su propia cuenta de Athena. */}
      {/* En escritorio, Claude y ChatGPT uno al lado del otro; en móvil, uno debajo de otro. */}
      <div className='grid grid-cols-1 items-start gap-3 min-[768px]:grid-cols-2'>
        {(['claude', 'chatgpt'] as const).map(asistente => (
          <details key={asistente} open={asistenteInicial === asistente} className='asistente-pasos group rounded-[3px] border border-(--line)'>
            <summary className='cursor-pointer list-none px-3 py-2 font-(family-name:--text) text-[16.5px] text-(--ink) marker:hidden'>
              <span aria-hidden='true' className='mr-2 inline-block text-(--teal-deep) transition-transform group-open:rotate-90'>
                ›
              </span>
              {t[asistente].titulo}
            </summary>
            <div className='flex flex-col gap-4 px-3 pt-1 pb-3'>
              {(['personal', 'empresa'] as const).map(cuenta => (
                <div key={cuenta} className='flex flex-col gap-1.5'>
                  <h3 className='m-0 font-(family-name:--text) text-[16px] font-semibold text-(--ink)'>{t[asistente][cuenta].titulo}</h3>
                  <ol className='m-0 flex list-decimal flex-col gap-1.5 pl-6 marker:text-(--ink-faint) font-(family-name:--text) text-[16px] leading-[1.5] text-(--ink-soft)'>
                    {t[asistente][cuenta].pasos.map(paso => (
                      <li key={paso}>{paso}</li>
                    ))}
                  </ol>
                  <p className='m-0 font-(family-name:--text) text-[15px] leading-[1.5] text-(--ink-faint)'>{t[asistente][cuenta].nota}</p>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>

      {/* Codex y Claude Code sí se instalan el conector solos si se les pide: un texto para copiar. */}
      <details className='asistente-pasos group rounded-[3px] border border-(--line)'>
        <summary className='cursor-pointer list-none px-3 py-2 font-(family-name:--text) text-[16.5px] text-(--ink) marker:hidden'>
          <span aria-hidden='true' className='mr-2 inline-block text-(--teal-deep) transition-transform group-open:rotate-90'>
            ›
          </span>
          {t.agentes.titulo}
        </summary>
        <div className='flex flex-col gap-2 px-3 pt-1 pb-3'>
          <p className='m-0 font-(family-name:--text) text-[16px] leading-[1.5] text-(--ink-soft)'>{t.agentes.aclaracion}</p>
          <p
            aria-label={t.agentes.etiqueta}
            className='m-0 rounded-[3px] border border-(--line-strong) bg-(--chat-bg) px-3 py-2 font-(family-name:--mono) text-[14px] leading-[1.55] text-(--ink) [overflow-wrap:anywhere]'
          >
            {promptAgente}
          </p>
          <button
            type='button'
            onClick={() => {
              copiar(promptAgente, 'prompt');
            }}
            className='self-start cursor-pointer rounded-full border border-(--line-strong) px-4 py-2 font-(family-name:--text) text-[16px] text-(--ink) hover:bg-(--chat-bg)'
          >
            {copiado === 'prompt' ? t.copiado : t.copiar}
          </button>
        </div>
      </details>

      <p className='m-0 font-(family-name:--text) text-[16px] text-(--ink-soft)'>{t.cadaPersona}</p>
      <p className='m-0 font-(family-name:--text) text-[15px] text-(--ink-faint)'>{t.nota}</p>
    </section>
  );
}
